














































































































































































































































































































// @ts-ignore
import v8n from 'v8n';
import moment from 'moment';
import { Component, Prop, Vue } from 'vue-property-decorator';
import {
  User,
  Bid,
  ApiError,
  Group,
  StaffVerificationResend,
  VerificationOverride,
} from '@/api';
import { snackModule, groupModule, authModule } from '@/store';
import ConfirmDialog from '@/components/admin/ConfirmDialog.vue';
import AbstractTable from '@/components/admin/AbstractTable.vue';

@Component({
  components: {
    ConfirmDialog,
    AbstractTable,
  },
})
export default class extends Vue {
  @Prop({ required: false }) readonly routeTitle: string;

  loading: boolean = false;
  valid: boolean = false;
  complete: boolean = false;
  error: string = '';

  passwordChars: any[] = [
    {
      name: 'Lowercase',
      value: 'abcdefghjklmnpqrstuvwxyz',
    },
    {
      name: 'Uppercase',
      value: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
    },
    {
      name: 'Numbers',
      value: '23456789',
    },
    {
      name: 'Special Characters',
      value: '_-+=)(*&^%$#@!`~',
    },
  ];
  passwordLength: number = 16;
  showPassword: boolean = false;

  user: User | null = null;

  isStaff: boolean = false;
  groupId: string | null = '';

  backConfirmDialog: boolean = false;

  resendDialog: boolean = false;
  overrideDialog: boolean = false;
  verificationLoading: boolean = false;
  verificationMessage: string = '';
  verificationMessageType: string = 'info';

  // Verification status is held here rather than read straight off `user`, for
  // two reasons. An override changes it behind the form's back, and writing the
  // new values onto the spraypaint instance would mark it dirty - which would
  // then prompt "unsaved changes" on Cancel over fields the server owns. And
  // whether Vue observes spraypaint's attribute accessors is an implementation
  // detail of both libraries, the same reason `views/AccountNotices.vue` keeps
  // local state alongside the store.
  // The address as the server holds it, so a save can tell whether it moved
  // (P4-11). Local for the same reason as `verification` below.
  originalEmail: string = '';

  verification: {
    isVerified: boolean;
    verifiedAt: string;
    verifiedManually: boolean;
    verifiedByName: string;
  } = {
    isVerified: false,
    verifiedAt: '',
    verifiedManually: false,
    verifiedByName: '',
  };

  stateOptions = [
    'Australian Capital Territory',
    'New South Wales',
    'Northern Territory',
    'Queensland',
    'South Australia',
    'Tasmania',
    'Victoria',
    'Western Australia',
  ];

  requiredString = [
    (v: string) =>
      v8n()
        .string()
        .not.empty()
        .test(v) || 'This field is required',
  ];

  requiredNumber = [
    (v: string) =>
      v8n()
        .number()
        .greaterThan(0)
        .test(Number(v)) || 'This field is required',
  ];

  passwordRules = [
    (v: string) =>
      v8n()
        .string()
        .not.empty()
        .test(v) || 'Password required',
    (v: string) =>
      v8n()
        .string()
        .minLength(8)
        .test(v) || 'Password must be at least 8 chars',
    (v: string) =>
      v8n()
        .not.numeric()
        .minLength(8)
        .test(v) || 'Password must include non-numeric chars',
  ];

  bidHeaders = [
    {
      text: 'Auction Code',
      value: 'lot.auction.code',
      width: 130,
    },
    {
      text: 'Lot Number',
      value: 'lot.number',
      width: 130,
    },
    {
      text: 'Lot',
      value: 'lot.title',
    },
    {
      text: 'Value',
      value: 'value',
      width: 150,
    },
    {
      text: 'Hammer Price',
      value: 'lot.salePrice',
      width: 150,
    },
  ];

  get bidScope() {
    return () => {
      if (!this.user) {
        return;
      }
      return Bid.includes(['lot.auction']).where({ user: this.user.id });
    };
  }

  get bidOptions() {
    return {
      sortBy: ['created'],
      sortDesc: [true],
      itemsPerPage: 40,
    };
  }

  formatValue(value: number) {
    if (!value) {
      return;
    }
    const dollarValue = value / 100;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(dollarValue);
  }

  get userId() {
    return this.$route.params.userId;
  }

  // Only on an existing user, and only for an admin. The `/admin` route tree is
  // already gated on the Administrators group by the router, and P5-03 refuses
  // both endpoints without `users.change_user` - this is belt and braces, so a
  // button that would always fail is never drawn.
  get showStaffTools() {
    return Boolean(this.user && this.userId && authModule.isAdmin);
  }

  get userEmail() {
    return this.user ? this.user.email : '';
  }

  get verifiedAtText() {
    if (!this.verification.verifiedAt) {
      return '';
    }
    return ` on ${moment(this.verification.verifiedAt).format(
      'D MMM YYYY, h:mma',
    )}`;
  }

  // Empty when the reader lacks `users.view_user`, or when the staff account has
  // since been deleted. Either way the sentence still reads, without claiming
  // nobody did it.
  get verifiedByText() {
    if (!this.verification.verifiedByName) {
      return '';
    }
    return ` by ${this.verification.verifiedByName}`;
  }

  // Case-insensitive, matching the server, which casefolds the address - fixing
  // the capitalisation is not a change and unverifies nobody. An emptied field
  // is not a change either: there is no new address to name in the warning, and
  // the required rule is what should speak.
  get emailChanged(): boolean {
    const email = this.user ? this.user.email || '' : '';
    if (!email || !this.originalEmail) {
      return false;
    }
    return email.toLowerCase() !== this.originalEmail.toLowerCase();
  }

  readVerification(user: User) {
    this.originalEmail = user.email || '';
    this.verification = {
      isVerified: Boolean(user.isVerified),
      verifiedAt: user.verifiedAt || '',
      verifiedManually: Boolean(user.verifiedManually),
      verifiedByName: user.verifiedByName || '',
    };
  }

  /**
   * Report a refusal in the words the server used.
   *
   * spraypaint throws on these rather than returning false: only a 422 counts
   * as a validation response, so a 400 (`already_verified`) or a 429 (the
   * shared resend cooldown) arrives as a `ResponseError` carrying the errors
   * array. Both are outcomes to report, not faults - hence the server's own
   * message rather than a generic failure.
   */
  reportApiError(err: any, fallback: string) {
    const errors: ApiError[] =
      (err && err.response && err.response.errors) || [];
    const detail = errors
      .map(e => e.detail)
      .filter(Boolean)
      .join(' ');
    this.verificationMessage = detail || fallback;
    this.verificationMessageType = 'warning';
  }

  async resendVerification() {
    this.resendDialog = false;
    if (!this.user) {
      return;
    }
    this.verificationLoading = true;
    this.verificationMessage = '';
    try {
      await StaffVerificationResend.send(this.user);
      this.verificationMessage = `Verification email sent to ${this.userEmail}.`;
      this.verificationMessageType = 'success';
    } catch (err) {
      this.reportApiError(err, 'The verification email could not be sent.');
    } finally {
      this.verificationLoading = false;
    }
  }

  async markVerified() {
    this.overrideDialog = false;
    if (!this.user) {
      return;
    }
    this.verificationLoading = true;
    this.verificationMessage = '';
    try {
      const { user, overriddenAt } = await VerificationOverride.mark(this.user);
      // The 201 sideloads the updated user, so the status re-renders without a
      // second request. Test `isVerified` rather than merely that a user came
      // back: `override.user` is the instance we sent, so it is truthy even if
      // the include never arrived, and trusting it blindly would redraw the
      // stale "Not verified" we started from. The fallback states the two things
      // the server has certainly just done.
      if (user && user.isVerified) {
        this.readVerification(user);
      } else {
        this.verification.isVerified = true;
        this.verification.verifiedManually = true;
      }
      const when = overriddenAt
        ? moment(overriddenAt).format('D MMM YYYY, h:mma')
        : 'now';
      this.verificationMessage = `Marked verified by hand, recorded against your name at ${when}.`;
      this.verificationMessageType = 'success';
    } catch (err) {
      this.reportApiError(err, 'The account could not be marked verified.');
    } finally {
      this.verificationLoading = false;
    }
  }

  get currentUser() {
    if (!authModule.user) {
      return null;
    }

    return authModule.user.id;
  }

  generatePassword() {
    if (!this.user) {
      return;
    }

    let result = '';
    let charactersVal = '';

    for (const chars of this.passwordChars) {
      charactersVal += chars.value;
    }

    for (let i = 0; i < this.passwordLength; i++) {
      result += charactersVal.charAt(
        Math.floor(Math.random() * charactersVal.length),
      );
    }

    this.user.password = result;
  }

  confirmBack() {
    if (this.user && this.user.isDirty()) {
      this.backConfirmDialog = true;
    } else {
      this.back();
    }
  }

  back() {
    this.$router.back();
  }

  async save() {
    if (!this.user) {
      return;
    }

    if (this.$route.name === 'admin-users-edit' && this.userId) {
      try {
        if (this.isStaff) {
          this.groupId = await groupModule.getGroup('Administrators');
        } else {
          this.groupId = await groupModule.getGroup('Users');
        }

        this.user.groups = [
          new Group({
            id: this.groupId,
          }),
        ];
      } catch {
        // Error
      }
    }

    const changingEmail = this.emailChanged;

    try {
      await this.user.save({ with: 'groups.id' });
      if (this.$route.name === 'admin-users-add' && this.user && this.user.id) {
        this.$router.push({
          name: 'admin-users-edit',
          params: { userId: this.user.id },
        });
        snackModule.showSnackbar('User Created');
      } else if (changingEmail) {
        // The server has just unverified the account and mailed both addresses
        // (P4-11). `verification` is local state, so it has to be told - reading
        // it back off the instance is what the comment on that field warns
        // against.
        this.originalEmail = this.user.email;
        this.verification = {
          isVerified: false,
          verifiedAt: '',
          verifiedManually: false,
          verifiedByName: '',
        };
        // Short enough to read in the time a snackbar gives it. The detail -
        // that the account is now unconfirmed and both addresses were mailed -
        // is on screen already, in the verification card and the warning above
        // the field, so the toast only has to say the address moved.
        snackModule.showSnackbar(
          `Address changed to ${this.user.email}. Confirmation link sent.`,
        );
      } else {
        snackModule.showSnackbar('User Updated');
      }
    } catch (err) {
      err.response.errors.forEach((e: ApiError) =>
        snackModule.showSnackbar(e.detail),
      );
    }
  }

  async mounted() {
    if (this.$route.name === 'admin-users-edit' && this.userId) {
      try {
        this.user = (await User.includes(['groups']).find(this.userId)).data;
        this.readVerification(this.user);

        const isAdmin = this.user.groups.some(
          group => group.name === 'Administrators',
        );
        if (isAdmin) {
          this.isStaff = true;
        }
      } catch (err) {
        // most likely this means not found
        snackModule.showSnackbar('Probably not found');
      }
    } else {
      this.user = new User();
    }
  }
}
